import API from '../AxiosInstance';
import { executeChunkedRequests, BackendLimits } from '@/components/utils/request-chunking';

export default {
  getAccessPoints: function() {
    const body = {
      // headers, body parameters, query parameters
    };

    return API.get('nodes', body);
  },

  getNodeByID: function(id) {
    const body = {
        // headers, body parameters, query parameters
    };
    return API.get('nodes/' + id, body);
  },

  createNode: function(params) {
    let formData = new FormData();

    for ( var key in params ) {
      formData.append(key, params[key]);
    }

    const options = {
      // headers, body parameters, query parameters
      headers: {
        "Content-Type": "multipart/form-data; boundary=file",
      },
    };
    return API.post('nodes', formData,  options);
  },

  updateNode: function(params, id) {
    let formData = new FormData();

    for (const key in params ) {
      //This is done because FormData casts null to the string "null", better to not send key-value pair in this case
      if (params[key] !== null) {
        formData.append(key, params[key]);
      }
    }

    params.id = id;
    const options = {
      headers: {
        "Content-Type": "multipart/form-data; boundary=file",
      },
    };
    return API.patch('nodes/' + id, formData, options);
  },

  updateFolder: function(id, data) {
    const body = data;
    return API.patch('nodes/' + id + '/folders', body);
  },

  /**
   * @param {string} id
   * @param {boolean} permanently
   * @returns {Promise<unknown>}
   */
  deleteNode: function(id, permanently ) {
    const body = {
      permanently,
    };

    return API.post(`nodes/${id}/delete`, body).then((r) => r.data);
  },

  /**
   *
   * @param {string[]} ids
   * @param {boolean} permanently
   * @returns {Promise<unknown>}
   */
  deleteManyNode: async function(ids, permanently) {
    const result = await executeChunkedRequests({
      items: ids,
      chunkSize: BackendLimits.MAX_NODES_PER_OPERATION,
      requestFn: async (chunk) => {
        const response = await API.post('nodes/bulk-delete', { nodes: chunk, permanently });
        return response.data || response;
      },
      aggregateFn: (results) => results.flat(),
      errorStrategy: { stopOnError: true },
    });

    if (!result.success && !result.partialSuccess) {
      throw result.errors[0]?.error;
    }
    return result.data;
  },

  deleteLogo: function(id) {
    const body = {};
    return API.delete('/nodes/'+id+'/logo', body);
  },

  softDeleteTenant: function(id) {
    const body = {};
    return API.delete('tenants/' + id + '/soft', body);
  },

  getAttachedUsers: function(nodeIDs) {
    const params = new URLSearchParams();
    nodeIDs.forEach((id) => {
      params.append('n[]', id);
    });

    return API.get(`nodes/users/?${params.toString()}`)
      .then((response) => {
        return response?.data || { activeUsers: 0, disabledUsers: 0 };
      });
  },

  getSubnodes: function(nodeID, subnodesType) {
    let url = 'nodes/'+nodeID+'/subnodes';
    if (subnodesType !== null) {
      url += '/'+subnodesType;
    }

    const body = {};

    return API.get(url, body);
  },

  moveManyNodes: async function(data) {
    const { nodes, ...otherParams } = data;

    const result = await executeChunkedRequests({
      items: nodes,
      chunkSize: BackendLimits.MAX_NODES_PER_OPERATION,
      requestFn: async (chunk) => {
        const response = await API.patch('nodes/move', { nodes: chunk, ...otherParams });
        return response.data || response;
      },
      aggregateFn: (results) => {
        if (results.length === 0) return { validation: false, nodes: [] };
        if (results[0]?.validation === true) {
          return {
            validation: true,
            messages: results.flatMap(r => r?.messages || []),
          };
        }
        return {
          validation: false,
          nodes: results.flatMap(r => r?.nodes || []),
        };
      },
      errorStrategy: { stopOnError: true },
    });

    if (!result.success && !result.partialSuccess) {
      throw result.errors[0]?.error;
    }
    return { data: result.data };
  },

  copyManyNodes: async function(data) {
    const { nodes, ...otherParams } = data;

    const result = await executeChunkedRequests({
      items: nodes,
      chunkSize: BackendLimits.MAX_NODES_PER_OPERATION,
      requestFn: async (chunk) => {
        const response = await API.post('nodes/copy', { nodes: chunk, ...otherParams });
        return response.data || response;
      },
      aggregateFn: (results) => ({
        copiedNodes: results.flatMap(r => r?.copiedNodes || []),
        unprocessablePods: results.flatMap(r => r?.unprocessablePods || []),
      }),
      errorStrategy: { stopOnError: true },
    });

    if (!result.success && !result.partialSuccess) {
      throw result.errors[0]?.error;
    }
    return result.data;
  },

  copyManyNodesCrossTenant: async function(data) {
    const { nodes, ...otherParams } = data;

    const result = await executeChunkedRequests({
      items: nodes,
      chunkSize: BackendLimits.MAX_NODES_PER_OPERATION,
      requestFn: async (chunk) => {
        const response = await API.post('nodes/copy-cross-tenant', { nodes: chunk, ...otherParams });
        return response.data || response;
      },
      aggregateFn: (results) => ({
        copiedNodes: results.flatMap(r => r?.copiedNodes || []),
        unprocessablePods: results.flatMap(r => r?.unprocessablePods || []),
      }),
      errorStrategy: { stopOnError: true },
    });

    if (!result.success && !result.partialSuccess) {
      throw result.errors[0]?.error;
    }
    return result.data;
  },

  updateNote: function(payload) {
    const body = {
      note: payload.note
    };

    return API.patch('nodes/' + payload.id + '/note', body);
  },

  updateProjectStatus: function(params, id) {
    const body = params;
    return API.patch('nodes/'+ id + '/projectstatus', body);
  },

  renameNode: function(payload) {
    const body = {
        name: payload.name,
    };

    return API.patch('nodes/' + payload.id + '/name', body);
  },

  getProperties: function(nodeID) {
    const body = {};
    return API.get('nodes/' + nodeID + '/properties', body);
  },

  getNodeInfo: function(nodeID) {
    return API.get('nodes/' + nodeID + '/info');
  },

  updateTeamOnly: function (nodeId, data) {
    const body = {
      teamonly: data.teamonly,
    };

    return API.patch('nodes/' + nodeId + '/teamonly', body)
      .then((response) => {
        return response.data;
      });
  },
  updateTeamOnlyNodes: async function(data) {
    const teamonly = data.teamonly ? 1 : 0;

    const result = await executeChunkedRequests({
      items: data.nodes,
      chunkSize: BackendLimits.MAX_NODES_PER_OPERATION,
      requestFn: async (chunk) => {
        const response = await API.patch('nodes/bulk-teamonly', { nodes: chunk, teamonly });
        return response.data || response;
      },
      aggregateFn: (results) => results.flat(),
      errorStrategy: { stopOnError: true },
    });

    if (!result.success && !result.partialSuccess) {
      throw result.errors[0]?.error;
    }
    return result.data;
  },
  /**
   * @param {[
   *   {
   *   teamonly: boolean,
   *   useruuid: string,
   *   nodes: ({
   *     key: string,
   *     nodeid: string,
   *     relativePath: string,
   *   })[],
   * }
   * ]} data
   * @returns {Promise<axios.AxiosResponse<any>>}
   */
  createManyNodes: function(data) {
    return API.post('nodes/create-many', data);
  },
};
